import React, { useState, useEffect, useRef } from 'react';
import { getArbicsxSettings, updateArbicsxSettings, uploadArbicsxProfilePhoto, deleteArbicsxProfilePhoto } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Plus, Trash2, Save, Upload, DollarSign, Camera, Eye, EyeOff, Key, RefreshCw, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use relative URL for API calls to avoid CORS issues in production
const API = '';

// Tappa 3: voci della navigazione laterale (solo aspetto)
const SETTINGS_PANELS = [
  { id: 'foto', label: 'Foto Profilo', short: 'Foto', icon: Camera },
  { id: 'profili', label: 'Profili Arbicsx', short: 'Profili', icon: Key },
  { id: 'wallet', label: 'Wallet TrustWallet', short: 'Wallet', icon: DollarSign },
  { id: 'fp', label: 'Credenziali FP Trading', short: 'FP', icon: Key },
  { id: 'report', label: 'Genera Report', short: 'Report', icon: FileText },
];

export default function ArbicsxImpostazioni() {
  const { token, user, refreshUser } = useAuth();
  const { impersonatedUser, isImpersonating, updateImpersonatedUser } = useModule();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localProfilePhoto, setLocalProfilePhoto] = useState(null); // Local state for immediate UI update
  const [activePanel, setActivePanel] = useState('foto');
  
  const photoInputRef = useRef(null);
  
  // Arbicsx accounts state
  const [arbicsxAccounts, setArbicsxAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({ email: '', password: '', label: '' });
  const [savingAccount, setSavingAccount] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [showAccountPasswords, setShowAccountPasswords] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // Broker account per-profile state
  const [accountBrokerNumbers, setAccountBrokerNumbers] = useState({});
  const [savingAccountBroker, setSavingAccountBroker] = useState({});
  
  // Report generation state
  const [reportPeriodType, setReportPeriodType] = useState('full');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [reportTitle, setReportTitle] = useState('Report Storico Completo');
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // TrustWallet address state
  const [trustwalletAddress, setTrustwalletAddress] = useState('');
  const [savingTrustwalletAddress, setSavingTrustwalletAddress] = useState(false);

  // FP Trading credentials state
  const [fpEmail, setFpEmail] = useState('');
  const [fpPassword, setFpPassword] = useState('');
  const [fpPin, setFpPin] = useState('');
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [showFpPin, setShowFpPin] = useState(false);
  const [savingFpCredentials, setSavingFpCredentials] = useState(false);
  
  const impersonateId = isImpersonating ? impersonatedUser?.id : null;
  const currentUser = isImpersonating ? impersonatedUser : user;
  const currentUserId = currentUser?.id;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getArbicsxSettings(impersonateId);
      setSettings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Arbicsx accounts
  const fetchAccounts = async () => {
    if (!currentUserId) return;
    setLoadingAccounts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/arbicsx/users/${currentUserId}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const accounts = response.data.accounts || [];
      setArbicsxAccounts(accounts);
      // Initialize broker account numbers per profile
      const brokerNumbers = {};
      accounts.forEach(acc => {
        brokerNumbers[acc.id] = acc.broker_account_number || '';
      });
      setAccountBrokerNumbers(brokerNumbers);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Save broker account number for specific Arbicsx profile
  const handleSaveAccountBroker = async (accountId) => {
    setSavingAccountBroker(prev => ({ ...prev, [accountId]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/arbicsx/users/${currentUserId}/accounts/${accountId}/broker-account`, {
        broker_account_number: accountBrokerNumbers[accountId] || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setArbicsxAccounts(prev => prev.map(acc => 
        acc.id === accountId 
          ? { ...acc, broker_account_number: accountBrokerNumbers[accountId] || '' }
          : acc
      ));
      toast.success('N° Conto Broker salvato');
    } catch (error) {
      toast.error('Errore nel salvataggio N° Conto Broker');
    } finally {
      setSavingAccountBroker(prev => ({ ...prev, [accountId]: false }));
    }
  };

  // Add new Arbicsx account
  const handleAddAccount = async () => {
    if (!newAccountForm.email || !newAccountForm.password) {
      toast.error('Email e Password sono obbligatori');
      return;
    }
    setSavingAccount(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/arbicsx/users/${currentUserId}/accounts`, {
        email: newAccountForm.email,
        password: newAccountForm.password,
        label: newAccountForm.label || `Profilo ${arbicsxAccounts.length + 1}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArbicsxAccounts([...arbicsxAccounts, response.data.account]);
      setNewAccountForm({ email: '', password: '', label: '' });
      setShowNewAccountForm(false);
      toast.success('Profilo Arbicsx aggiunto');
    } catch (error) {
      toast.error('Errore nell\'aggiunta del profilo');
    } finally {
      setSavingAccount(false);
    }
  };

  // Delete Arbicsx account
  const handleDeleteAccount = async (accountId) => {
    setDeletingAccountId(accountId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/arbicsx/users/${currentUserId}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArbicsxAccounts(arbicsxAccounts.filter(acc => acc.id !== accountId));
      setConfirmDeleteId(null);
      toast.success('Profilo Arbicsx eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione del profilo');
    } finally {
      setDeletingAccountId(null);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (accountId) => {
    setShowAccountPasswords(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  useEffect(() => {
    fetchData();
    fetchAccounts();
  }, [impersonateId, currentUserId]);

  // Initialize TrustWallet and FP credentials when user changes
  useEffect(() => {
    if (currentUser) {
      setTrustwalletAddress(currentUser.trustwallet_address || '');
      setFpEmail(currentUser.fp_email || '');
      setFpPassword(currentUser.fp_password || '');
      setFpPin(currentUser.fp_pin || '');
    }
  }, [currentUser]);

  // Save TrustWallet address
  const handleSaveTrustwalletAddress = async () => {
    if (!currentUserId) return;
    setSavingTrustwalletAddress(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${currentUserId}/trustwallet-address`, {
        trustwallet_address: trustwalletAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indirizzo TrustWallet salvato con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio indirizzo TrustWallet');
    } finally {
      setSavingTrustwalletAddress(false);
    }
  };

  // Save FP Trading credentials
  const handleSaveFpCredentials = async () => {
    if (!currentUserId) return;
    setSavingFpCredentials(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${currentUserId}/fp-credentials`, {
        fp_email: fpEmail,
        fp_password: fpPassword,
        fp_pin: fpPin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credenziali FP Trading salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio credenziali FP Trading');
    } finally {
      setSavingFpCredentials(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateArbicsxSettings(settings, impersonateId);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Profile Photo handlers
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG o WebP');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const response = await uploadArbicsxProfilePhoto(file, impersonateId);
      const newPhotoUrl = response.data?.profile_photo;
      
      toast.success('Foto profilo caricata');
      
      // Update local state immediately for UI feedback
      if (newPhotoUrl) {
        setLocalProfilePhoto(newPhotoUrl);
      }
      
      // Update the appropriate context based on impersonation
      if (isImpersonating && updateImpersonatedUser) {
        updateImpersonatedUser({ profile_photo: newPhotoUrl });
      } else if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await deleteArbicsxProfilePhoto(impersonateId);
      toast.success('Foto profilo rimossa');
      
      // Clear local state
      setLocalProfilePhoto(null);
      
      // Update the appropriate context
      if (isImpersonating && updateImpersonatedUser) {
        updateImpersonatedUser({ profile_photo: null });
      } else if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // Report generation functions
  const handleReportPeriodTypeChange = (type) => {
    setReportPeriodType(type);
    if (type === 'full') {
      setReportTitle('Report Storico Completo');
    } else {
      const fromFormatted = reportDateFrom ? new Date(reportDateFrom).toLocaleDateString('it-IT') : '';
      const toFormatted = reportDateTo ? new Date(reportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
      setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
    }
  };

  const handleReportDateChange = (field, value) => {
    if (field === 'from') {
      setReportDateFrom(value);
      if (reportPeriodType === 'custom') {
        const fromFormatted = value ? new Date(value).toLocaleDateString('it-IT') : '';
        const toFormatted = reportDateTo ? new Date(reportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
      }
    } else {
      setReportDateTo(value);
      if (reportPeriodType === 'custom') {
        const fromFormatted = reportDateFrom ? new Date(reportDateFrom).toLocaleDateString('it-IT') : '';
        const toFormatted = value ? new Date(value).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
      }
    }
  };

  const handleGenerateReport = async () => {
    if (!currentUserId) return;
    setGeneratingReport(true);
    try {
      const payload = {
        title: reportTitle,
        date_from: reportPeriodType === 'custom' ? reportDateFrom : null,
        date_to: reportPeriodType === 'custom' ? reportDateTo : null
      };
      const response = await axios.post(`${API}/api/arbicsx/users/${currentUserId}/generate-report`, payload);
      if (response.data.success) {
        toast.success(`Report inviato su Telegram ✅ (${response.data.profits_count} profitti)`);
      } else if (response.data.error === "Utente senza Telegram collegato") {
        toast.warning('Collega il tuo account Telegram nelle impostazioni per ricevere i report');
      } else {
        toast.error(response.data.error || 'Errore nella generazione del report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Impostazioni</h1>
          <p className="ht-page-sub">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSaveSettings}
          className="ht-btn ht-btn-primary self-start sm:self-auto"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          <Save size={14} />
          {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
        </button>
      </div>

      {/* Navigazione laterale + un solo pannello alla volta */}
      <div className="flex flex-col lg:flex-row gap-3">
        <aside className="grid grid-cols-3 sm:grid-cols-5 gap-2 lg:flex lg:flex-col lg:w-[208px] lg:flex-shrink-0" data-testid="settings-menu">
          {SETTINGS_PANELS.map(panel => {
            const Icon = panel.icon;
            const active = activePanel === panel.id;
            return (
              <button
                key={panel.id}
                type="button"
                onClick={() => setActivePanel(panel.id)}
                data-testid={`settings-panel-${panel.id}`}
                className={`ht-tile lg:hidden ${active ? 'ht-tile-active' : ''}`}
              >
                <Icon size={15} />
                <span>{panel.short}</span>
              </button>
            );
          })}
          {SETTINGS_PANELS.map(panel => {
            const Icon = panel.icon;
            const active = activePanel === panel.id;
            return (
              <button
                key={`d-${panel.id}`}
                type="button"
                onClick={() => setActivePanel(panel.id)}
                data-testid={`settings-panel-desktop-${panel.id}`}
                className={`ht-side-item hidden lg:flex ${active ? 'ht-side-item-active' : ''} ${active ? 'neon n2' : ''}`}
              >
                <Icon size={14} />
                <span className="leading-tight">{panel.label}</span>
              </button>
            );
          })}
        </aside>

        <div className="flex-1 min-w-0" data-testid="settings-panel">
          {/* Foto Profilo */}
          {activePanel === 'foto' && (
            <div className="ht-panel p-4">
              <div className="flex items-center gap-2 mb-4">
                <Camera size={14} className="text-[#5f6672]" />
                <h2 className="ht-panel-title text-sm">Foto Profilo</h2>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="relative self-start">
                  <Avatar className="w-20 h-20 ht-ring">
                    {(localProfilePhoto || currentUser?.profile_photo) ? (
                      <AvatarImage src={localProfilePhoto || currentUser.profile_photo} alt="Foto profilo" />
                    ) : (
                      <AvatarFallback className="bg-[#11161d] text-lg ht-pos">
                        {getInitials(currentUser?.name)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="ht-btn ht-btn-icon absolute bottom-0 right-0"
                    aria-label="Cambia foto"
                    disabled={uploadingPhoto}
                  >
                    <Camera size={12} />
                  </button>
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#8b919c] mb-3">
                    Carica una foto profilo. Formati: JPG, PNG, WebP. Max 5MB.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="ht-btn"
                      disabled={uploadingPhoto}
                      data-testid="upload-photo-btn"
                    >
                      <Upload size={13} />
                      {uploadingPhoto ? 'Caricamento...' : 'Carica foto'}
                    </button>
                    {(localProfilePhoto || currentUser?.profile_photo) && (
                      <button
                        type="button"
                        onClick={handleDeletePhoto}
                        className="ht-btn ht-btn-danger"
                        data-testid="delete-photo-btn"
                      >
                        <Trash2 size={13} />
                        Rimuovi
                      </button>
                    )}
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Profili Arbicsx */}
          {activePanel === 'profili' && (
            <div className="ht-panel p-4">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <Key size={14} className="text-[#5f6672]" />
                  <h2 className="ht-panel-title text-sm">Profili Arbicsx</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowNewAccountForm(!showNewAccountForm)}
                  className="ht-btn ht-btn-sm ht-btn-primary"
                  data-testid="add-account-btn"
                >
                  <Plus size={13} />
                  Aggiungi
                </button>
              </div>

              {loadingAccounts ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw size={18} className="animate-spin text-[#5f6672]" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* New Account Form */}
                  {showNewAccountForm && (
                    <div className="rounded-[12px] border border-[#282c34] bg-[#11161d] p-3 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-[#8b919c]">Label (opzionale)</Label>
                          <Input
                            type="text"
                            value={newAccountForm.label}
                            onChange={(e) => setNewAccountForm({...newAccountForm, label: e.target.value})}
                            placeholder="Es. Profilo 1"
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-[#8b919c]">Email *</Label>
                          <Input
                            type="text"
                            value={newAccountForm.email}
                            onChange={(e) => setNewAccountForm({...newAccountForm, email: e.target.value})}
                            placeholder="email@arbicsx.com"
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-[#8b919c]">Password *</Label>
                          <Input
                            type="text"
                            value={newAccountForm.password}
                            onChange={(e) => setNewAccountForm({...newAccountForm, password: e.target.value})}
                            placeholder="password"
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-9"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => { setShowNewAccountForm(false); setNewAccountForm({ email: '', password: '', label: '' }); }}
                          className="ht-btn ht-btn-sm"
                        >
                          Annulla
                        </button>
                        <button
                          type="button"
                          onClick={handleAddAccount}
                          disabled={savingAccount}
                          className="ht-btn ht-btn-sm ht-btn-primary"
                          data-testid="save-account-btn"
                        >
                          {savingAccount ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                          Salva
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Accounts List */}
                  {arbicsxAccounts.length === 0 && !showNewAccountForm ? (
                    <div className="ht-empty">
                      <Key size={28} className="mx-auto mb-2 opacity-40" />
                      <p>Nessun profilo Arbicsx configurato</p>
                      <p className="text-xs mt-1">Clicca "Aggiungi" per creare il tuo primo profilo</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {arbicsxAccounts.map((account) => (
                        <div key={account.id} className="rounded-[12px] border border-[#282c34] bg-[#11161d] p-3 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="ht-acc-title truncate">{account.label || 'Profilo'}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="ht-tag">{account.ready_accounts || 0} conti pronti</span>
                              {confirmDeleteId === account.id ? (
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteAccount(account.id)}
                                    className="ht-btn ht-btn-sm ht-btn-danger"
                                    disabled={deletingAccountId === account.id}
                                  >
                                    {deletingAccountId === account.id ? <RefreshCw size={11} className="animate-spin" /> : 'Conferma'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="ht-btn ht-btn-sm"
                                  >
                                    Annulla
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(account.id)}
                                  className="ht-btn ht-btn-icon ht-btn-danger"
                                  aria-label="Elimina profilo"
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="ht-rows">
                            <div className="ht-row">
                              <span className="ht-row-label">Email</span>
                              <span className="ht-row-value truncate">{account.email}</span>
                            </div>
                            <div className="ht-row">
                              <span className="ht-row-label">Password</span>
                              <span className="flex items-center gap-1.5">
                                <span className="ht-row-value">
                                  {showAccountPasswords[account.id] ? account.password : '••••••••'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility(account.id)}
                                  className="ht-btn ht-btn-icon"
                                  aria-label="Mostra password"
                                >
                                  {showAccountPasswords[account.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                              </span>
                            </div>
                            <div className="ht-row flex-wrap">
                              <span className="ht-row-label">N° Conto</span>
                              <span className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                                <Input
                                  type="text"
                                  value={accountBrokerNumbers[account.id] || ''}
                                  onChange={(e) => setAccountBrokerNumbers(prev => ({ 
                                    ...prev, 
                                    [account.id]: e.target.value 
                                  }))}
                                  placeholder="Numero conto broker"
                                  className="bg-[#0d1117] border-[#282c34] text-white h-8 text-xs flex-1 min-w-0"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSaveAccountBroker(account.id)}
                                  disabled={savingAccountBroker[account.id]}
                                  className="ht-btn ht-btn-icon ht-btn-primary"
                                  aria-label="Salva numero conto"
                                >
                                  {savingAccountBroker[account.id] ? (
                                    <RefreshCw size={12} className="animate-spin" />
                                  ) : (
                                    <Save size={12} />
                                  )}
                                </button>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Wallet TrustWallet */}
          {activePanel === 'wallet' && (
            <div className="ht-panel p-4">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={14} className="text-[#5f6672]" />
                <h2 className="ht-panel-title text-sm">Wallet TrustWallet</h2>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-[#8b919c]">Indirizzo TrustWallet (ERC20)</Label>
                <div className="flex flex-wrap gap-2">
                  <Input
                    type="text"
                    value={trustwalletAddress}
                    onChange={(e) => setTrustwalletAddress(e.target.value)}
                    placeholder="0x..."
                    className="bg-[#11161d] border-[#282c34] text-white font-mono text-sm flex-1 min-w-0"
                    data-testid="trustwallet-input"
                  />
                  <button
                    type="button"
                    onClick={handleSaveTrustwalletAddress}
                    disabled={savingTrustwalletAddress}
                    className="ht-btn ht-btn-primary"
                    data-testid="trustwallet-save-btn"
                  >
                    {savingTrustwalletAddress ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                    Salva
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Credenziali FP Trading */}
          {activePanel === 'fp' && (
            <div className="ht-panel p-4">
              <div className="flex items-center gap-2 mb-4">
                <Key size={14} className="text-[#5f6672]" />
                <h2 className="ht-panel-title text-sm">Credenziali FP Trading</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-[#8b919c]">Email</Label>
                  <Input
                    type="email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    placeholder="Email FP Trading"
                    className="bg-[#11161d] border-[#282c34] text-white"
                    data-testid="fp-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#8b919c]">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showFpPassword ? "text" : "password"}
                      value={fpPassword}
                      onChange={(e) => setFpPassword(e.target.value)}
                      placeholder="Password FP Trading"
                      className="bg-[#11161d] border-[#282c34] text-white flex-1 min-w-0"
                      data-testid="fp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFpPassword(!showFpPassword)}
                      className="ht-btn ht-btn-icon"
                      aria-label="Mostra password"
                    >
                      {showFpPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#8b919c]">PIN</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showFpPin ? "text" : "password"}
                      value={fpPin}
                      onChange={(e) => setFpPin(e.target.value)}
                      placeholder="PIN FP Trading"
                      className="bg-[#11161d] border-[#282c34] text-white flex-1 min-w-0"
                      data-testid="fp-pin-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFpPin(!showFpPin)}
                      className="ht-btn ht-btn-icon"
                      aria-label="Mostra PIN"
                    >
                      {showFpPin ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSaveFpCredentials}
                  disabled={savingFpCredentials}
                  className="ht-btn ht-btn-primary w-full"
                  data-testid="fp-credentials-save-btn"
                >
                  {savingFpCredentials ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                  Salva Credenziali FP
                </button>
              </div>
            </div>
          )}

          {/* Genera Report */}
          {activePanel === 'report' && (
            <div className="ht-panel p-4" data-testid="report-generation-section">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={14} className="text-[#5f6672]" />
                <h2 className="ht-panel-title text-sm">Genera Report</h2>
              </div>
              <p className="text-xs text-[#8b919c]">Ricevi un PDF con il riepilogo dei tuoi profitti su Telegram</p>
              
              <div className="mt-4 space-y-4">
                {/* Period Type Selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-[#8b919c]">Tipo periodo</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleReportPeriodTypeChange('full')}
                      className={`ht-tab`}
                      data-state={reportPeriodType === 'full' ? 'active' : 'inactive'}
                      data-testid="report-period-full"
                    >
                      Storico completo
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReportPeriodTypeChange('custom')}
                      className={`ht-tab`}
                      data-state={reportPeriodType === 'custom' ? 'active' : 'inactive'}
                      data-testid="report-period-custom"
                    >
                      Periodo personalizzato
                    </button>
                  </div>
                </div>

                {/* Date Range (visible only for custom period) */}
                {reportPeriodType === 'custom' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-[#8b919c]">Dal</Label>
                      <Input
                        type="date"
                        value={reportDateFrom}
                        onChange={(e) => handleReportDateChange('from', e.target.value)}
                        className="bg-[#11161d] border-[#282c34] text-white"
                        data-testid="report-date-from"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-[#8b919c]">Al</Label>
                      <Input
                        type="date"
                        value={reportDateTo}
                        onChange={(e) => handleReportDateChange('to', e.target.value)}
                        className="bg-[#11161d] border-[#282c34] text-white"
                        data-testid="report-date-to"
                      />
                    </div>
                  </div>
                )}

                {/* Report Title */}
                <div className="space-y-2">
                  <Label className="text-xs text-[#8b919c]">Titolo del report</Label>
                  <Input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    placeholder="Titolo del report"
                    className="bg-[#11161d] border-[#282c34] text-white"
                    data-testid="report-title-input"
                  />
                </div>

                {/* Generate Button */}
                <button
                  type="button"
                  onClick={handleGenerateReport}
                  disabled={generatingReport}
                  className="ht-btn ht-btn-primary w-full sm:w-auto"
                  data-testid="generate-report-btn"
                >
                  {generatingReport ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {generatingReport ? 'Generazione in corso...' : 'Genera e Invia su Telegram'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
